import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { getGraphQLClient, UPDATE_BOOK, DELETE_BOOK } from '../../lib/graphql';
import { captureException } from '../../lib/sentry';
import { BOOK_TYPE_CONFIG, BOOK_TYPES } from '../../lib/bookTypeConfig';
import {
  Button,
  Input,
  AutoResizeTextarea,
  DescriptiveSelect,
  DescriptiveOption,
  ConfirmDialog,
} from '../common';
import { BookType, Book } from '../../types';

// Main themes (basic genres) - shown first
const MAIN_THEMES_BY_TYPE: Record<BookType, string[]> = {
  fiction: [
    'fantasy',
    'sci_fi',
    'romance',
    'mystery',
    'thriller',
    'horror',
    'adventure',
    'historical',
  ],
  non_fiction: [
    'biography',
    'history',
    'science',
    'self_help',
    'philosophy',
    'health',
    'travel',
    'cooking',
    'business',
    'technology',
    'true_crime',
  ],
};

// Subthemes for each main theme - shown after main theme selection
const SUBTHEMES: Record<string, string[]> = {
  // Fiction subthemes
  fantasy: ['epic_fantasy', 'urban_fantasy', 'dark_fantasy', 'fairy_tale', 'magical_realism'],
  sci_fi: ['space_opera', 'dystopian', 'cyberpunk', 'time_travel', 'post_apocalyptic'],
  romance: ['contemporary_romance', 'historical_romance', 'paranormal_romance', 'romantic_comedy'],
  mystery: ['detective', 'cozy_mystery', 'noir', 'whodunit', 'police_procedural'],
  thriller: ['psychological_thriller', 'spy_thriller', 'legal_thriller', 'crime_thriller'],
  horror: ['supernatural', 'psychological_horror', 'gothic', 'cosmic_horror', 'slasher'],
  adventure: ['action', 'survival', 'treasure_hunt', 'exploration', 'military'],
  historical: ['ancient', 'medieval', 'victorian', 'world_war', 'renaissance'],
  // Non-fiction subthemes
  biography: ['memoir', 'autobiography', 'celebrity_bio', 'historical_figure'],
  history: ['ancient_history', 'modern_history', 'military_history', 'cultural_history'],
  science: ['popular_science', 'nature', 'physics', 'biology', 'astronomy'],
  self_help: ['productivity', 'relationships', 'mindfulness', 'career', 'personal_growth'],
  philosophy: ['ethics', 'existentialism', 'stoicism', 'political_philosophy'],
  health: ['nutrition', 'fitness', 'mental_health', 'wellness'],
  travel: ['adventure_travel', 'cultural', 'guides', 'travel_memoir'],
  cooking: ['recipes', 'food_culture', 'baking', 'world_cuisine'],
  business: ['entrepreneurship', 'leadership', 'finance', 'marketing', 'management'],
  technology: ['programming', 'ai', 'internet', 'gadgets', 'future_tech'],
  true_crime: ['serial_killers', 'cold_cases', 'forensics', 'heists'],
};

// Age group presets: key => [minAge, maxAge]
// Mirrors backend Book::AGE_GROUP_PRESETS
const AGE_GROUP_PRESETS: Record<string, [number, number]> = {
  children: [3, 12],
  teenagers: [13, 17],
  young_adults: [18, 25],
  adults: [18, 100],
  all: [0, 100],
};

// Helper to find preset key from age range
function findPresetKey(
  ageFrom: number | null | undefined,
  ageTo: number | null | undefined
): string {
  // If both are null/undefined, it's "all ages"
  if (ageFrom == null && ageTo == null) {
    return 'all';
  }
  for (const [key, [min, max]] of Object.entries(AGE_GROUP_PRESETS)) {
    if (ageFrom === min && ageTo === max) {
      return key;
    }
  }
  return 'other'; // Custom range
}

interface BookBasicsStepProps {
  book: Book;
  onComplete: (book: Book) => void;
  onCancel: () => void;
  isEditMode?: boolean;
}

// Helper function to separate main themes from subthemes
function separateThemes(
  themes: string[],
  bookType: BookType
): { mainThemes: string[]; subthemes: string[] } {
  const mainThemeSet = new Set(MAIN_THEMES_BY_TYPE[bookType] || []);
  const mainThemes: string[] = [];
  const subthemes: string[] = [];

  themes.forEach((theme) => {
    if (mainThemeSet.has(theme)) {
      mainThemes.push(theme);
    } else {
      subthemes.push(theme);
    }
  });

  return { mainThemes, subthemes };
}

export default function BookBasicsStep({
  book,
  onComplete,
  onCancel,
  isEditMode = false,
}: BookBasicsStepProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const client = getGraphQLClient();
      await client.request(DELETE_BOOK, { id: book.id });
      navigate('/books');
    } catch (err) {
      captureException(err);
      setError(t('common.error'));
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Normalize bookType to lowercase (API returns uppercase like 'FICTION')
  const normalizeBookType = (type?: string): BookType =>
    (type?.toLowerCase() || 'fiction') as BookType;

  // Initialize state from existing book data
  const initialBookType = normalizeBookType(book.bookType);
  const initialThemes = separateThemes(book.themes || [], initialBookType);

  const [bookType, setBookType] = useState<BookType>(initialBookType);
  const [selectedMainThemes, setSelectedMainThemes] = useState<string[]>(initialThemes.mainThemes);
  const [selectedSubthemes, setSelectedSubthemes] = useState<string[]>(initialThemes.subthemes);
  const [customTheme, setCustomTheme] = useState('');
  const [isCustomThemeModalOpen, setIsCustomThemeModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [error, setError] = useState('');

  // More options state
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [bookLanguage, setBookLanguage] = useState(book.language || i18n.language || 'en');
  const [readerAgeFrom, setReaderAgeFrom] = useState<number | null>(book.readerAgeFrom ?? null);
  const [readerAgeTo, setReaderAgeTo] = useState<number | null>(book.readerAgeTo ?? null);
  const [selectedAgePreset, setSelectedAgePreset] = useState(() =>
    findPresetKey(book.readerAgeFrom, book.readerAgeTo)
  );
  const [additionalInstructions, setAdditionalInstructions] = useState(
    book.additionalInstructions || ''
  );

  // Handle age preset selection
  const handleAgePresetChange = (preset: string) => {
    setSelectedAgePreset(preset);
    if (preset === 'all') {
      // All ages means null values
      setReaderAgeFrom(null);
      setReaderAgeTo(null);
    } else if (preset === 'other') {
      // Keep current custom values or set defaults
      if (readerAgeFrom == null && readerAgeTo == null) {
        setReaderAgeFrom(5);
        setReaderAgeTo(10);
      }
    } else {
      const [from, to] = AGE_GROUP_PRESETS[preset] || [null, null];
      setReaderAgeFrom(from);
      setReaderAgeTo(to);
    }
  };

  const mainThemes = MAIN_THEMES_BY_TYPE[bookType] || [];

  // Get all available subthemes from all selected main themes
  const availableSubthemes = selectedMainThemes.flatMap((theme) => SUBTHEMES[theme] || []);

  // Combine main themes with subthemes for the final themes array
  const selectedThemes = [...selectedMainThemes, ...selectedSubthemes];

  // Sync state with book prop when it changes (e.g., when navigating back)
  useEffect(() => {
    if (book) {
      const normalizedType = normalizeBookType(book.bookType);
      const themes = separateThemes(book.themes, normalizedType);
      setBookType(normalizedType);
      setSelectedMainThemes(themes.mainThemes);
      setSelectedSubthemes(themes.subthemes);
      setBookLanguage(book.language || i18n.language || 'en');
      setAdditionalInstructions(book.additionalInstructions || '');

      // Sync age group fields
      const ageFrom = book.readerAgeFrom ?? 0;
      const ageTo = book.readerAgeTo ?? 100;
      setReaderAgeFrom(ageFrom);
      setReaderAgeTo(ageTo);
      setSelectedAgePreset(findPresetKey(ageFrom, ageTo));
    }
  }, [book, i18n.language]);

  const handleBookTypeChange = (newType: BookType) => {
    setBookType(newType);
    setSelectedMainThemes([]);
    setSelectedSubthemes([]);
  };

  const toggleMainTheme = (theme: string) => {
    setSelectedMainThemes((prev) => {
      if (prev.includes(theme)) {
        // When removing a main theme, also remove its subthemes
        const themeSubthemes = SUBTHEMES[theme] || [];
        setSelectedSubthemes((currentSubthemes) =>
          currentSubthemes.filter((s) => !themeSubthemes.includes(s))
        );
        return prev.filter((t) => t !== theme);
      } else {
        return [...prev, theme];
      }
    });
  };

  const toggleSubtheme = (subtheme: string) => {
    setSelectedSubthemes((prev) =>
      prev.includes(subtheme) ? prev.filter((t) => t !== subtheme) : [...prev, subtheme]
    );
  };

  const addCustomTheme = () => {
    const trimmed = customTheme.trim();
    if (trimmed && !selectedSubthemes.includes(trimmed)) {
      setSelectedSubthemes((prev) => [...prev, trimmed]);
      setCustomTheme('');
      setIsCustomThemeModalOpen(false);
    }
  };

  const removeSubtheme = (theme: string) => {
    setSelectedSubthemes((prev) => prev.filter((t) => t !== theme));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedThemes.length === 0) return;

    setIsLoading(true);
    setError('');

    try {
      const client = getGraphQLClient();

      // Only pass age if not default range
      const isDefaultAge = readerAgeFrom === 0 && readerAgeTo === 100;

      // Always update the existing book
      const data = await client.request<{
        updateBook: { book: Book; errors: string[] };
      }>(UPDATE_BOOK, {
        id: book.id,
        bookType: bookType.toUpperCase(),
        themes: selectedThemes,
        language: bookLanguage,
        readerAgeFrom: isDefaultAge ? null : readerAgeFrom,
        readerAgeTo: isDefaultAge ? null : readerAgeTo,
        additionalInstructions: additionalInstructions || null,
      });

      if (data.updateBook.errors.length > 0) {
        setError(data.updateBook.errors[0]);
      } else {
        onComplete(data.updateBook.book);
      }
    } catch (err) {
      captureException(err, {
        component: 'BookBasicsStep',
        action: 'saveBook',
        bookId: book.id,
        isUpdate: true,
      });
      setError(t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Book Type Selection */}
        <DescriptiveSelect
          label={t('stories.bookType')}
          value={bookType}
          onChange={(value) => handleBookTypeChange(value as BookType)}
          options={BOOK_TYPES.map((type) => ({
            value: type,
            label: t(`bookTypes.${type}`),
            description: t(BOOK_TYPE_CONFIG[type].descriptionKey),
            icon: BOOK_TYPE_CONFIG[type].icon('w-5 h-5'),
          }))}
        />

        {/* Main Theme Selection (Genre) - multiple selection */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            {t('stories.genre')} <span className="text-red-500">*</span>
          </label>
          <div className="flex flex-wrap gap-2">
            {mainThemes.map((theme) => (
              <button
                key={theme}
                type="button"
                onClick={() => toggleMainTheme(theme)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                  selectedMainThemes.includes(theme)
                    ? 'bg-primary-500 text-white shadow-md'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {t(`themes.${theme}`, theme)}
              </button>
            ))}
          </div>
        </div>

        {/* Subthemes Selection - shown after at least one main theme is selected */}
        <AnimatePresence mode="wait">
          {selectedMainThemes.length > 0 && availableSubthemes.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                {t('stories.subthemes')}
                <span className="text-gray-400 dark:text-gray-500 font-normal ml-2">
                  ({t('common.optional')})
                </span>
              </label>
              <div className="flex flex-wrap gap-2">
                {availableSubthemes.map((subtheme) => (
                  <button
                    key={subtheme}
                    type="button"
                    onClick={() => toggleSubtheme(subtheme)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                      selectedSubthemes.includes(subtheme)
                        ? 'bg-primary-500 text-white shadow-md'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {t(`themes.${subtheme}`, subtheme.replace(/_/g, ' '))}
                  </button>
                ))}
                {/* Custom subthemes - those not in availableSubthemes */}
                {selectedSubthemes
                  .filter((theme) => !availableSubthemes.includes(theme))
                  .map((theme) => (
                    <button
                      key={theme}
                      type="button"
                      onClick={() => removeSubtheme(theme)}
                      className="px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 bg-primary-500 text-white shadow-md"
                    >
                      {theme}
                    </button>
                  ))}
                <button
                  type="button"
                  onClick={() => setIsCustomThemeModalOpen(true)}
                  className="px-3 py-1.5 rounded-full text-sm font-medium border-2 border-dashed border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-primary-400 hover:text-primary-500 dark:hover:border-primary-500 dark:hover:text-primary-400 transition-all duration-200 flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  {t('stories.addCustomTheme')}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* More Options Section */}
        <div>
          <button
            type="button"
            onClick={() => setShowMoreOptions(!showMoreOptions)}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
          >
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {t('stories.moreOptions')}
            </span>
            <svg
              className={`w-5 h-5 text-gray-500 transition-transform ${showMoreOptions ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          <AnimatePresence>
            {showMoreOptions && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="mt-4 space-y-4 px-2">
                  {isEditMode && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                      {t(
                        'editBook.audienceMovedHint',
                        'Language and reader age now live on the Audience tab.'
                      )}
                    </p>
                  )}
                  {/* Book Language + Reader Age — wizard only. Edit mode uses Audience tab. */}
                  {!isEditMode && (
                  <>
                  <DescriptiveSelect
                    label={t('stories.bookLanguage')}
                    optionalText={t('common.optional')}
                    value={bookLanguage}
                    onChange={setBookLanguage}
                    options={
                      [
                        {
                          value: 'en',
                          label: t('languages.en.label'),
                          description: t('languages.en.description'),
                          icon: <span className="text-base">🇬🇧</span>,
                        },
                        {
                          value: 'lt',
                          label: t('languages.lt.label'),
                          description: t('languages.lt.description'),
                          icon: <span className="text-base">🇱🇹</span>,
                        },
                        {
                          value: 'ru',
                          label: t('languages.ru.label'),
                          description: t('languages.ru.description'),
                          icon: <span className="text-base">🇷🇺</span>,
                        },
                      ] as DescriptiveOption[]
                    }
                  />

                  {/* Reader Age Group */}
                  <DescriptiveSelect
                    label={t('stories.readerAgeGroup')}
                    optionalText={t('common.optional')}
                    value={selectedAgePreset}
                    onChange={handleAgePresetChange}
                    options={
                      [
                        {
                          value: 'all',
                          label: t('ageGroups.all.label'),
                          description: t('ageGroups.all.description'),
                          icon: (
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
                              />
                            </svg>
                          ),
                        },
                        {
                          value: 'children',
                          label: t('ageGroups.kids.label'),
                          description: t('ageGroups.kids.description'),
                          icon: (
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M15.182 15.182a4.5 4.5 0 01-6.364 0M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75zm-.375 0h.008v.015h-.008V9.75zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75zm-.375 0h.008v.015h-.008V9.75z"
                              />
                            </svg>
                          ),
                        },
                        {
                          value: 'teenagers',
                          label: t('ageGroups.teenagers.label'),
                          description: t('ageGroups.teenagers.description'),
                          icon: (
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5"
                              />
                            </svg>
                          ),
                        },
                        {
                          value: 'adults',
                          label: t('ageGroups.adults.label'),
                          description: t('ageGroups.adults.description'),
                          icon: (
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z"
                              />
                            </svg>
                          ),
                        },
                        {
                          value: 'other',
                          label: t('ageGroups.other.label'),
                          description: t('ageGroups.other.description'),
                          icon: (
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                            </svg>
                          ),
                        },
                      ] as DescriptiveOption[]
                    }
                  />

                  {/* Custom Age Range - shown when "other" is selected */}
                  <AnimatePresence>
                    {selectedAgePreset === 'other' && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
                            {t('ageGroups.customRange')}
                          </label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              value={readerAgeFrom ?? ''}
                              onChange={(e) => setReaderAgeFrom(parseInt(e.target.value) || 0)}
                              placeholder={t('ageGroups.from')}
                              min="0"
                              max="100"
                              className="flex-1 min-w-0 text-center"
                            />
                            <span className="text-gray-400 flex-shrink-0">—</span>
                            <Input
                              type="number"
                              value={readerAgeTo ?? ''}
                              onChange={(e) => setReaderAgeTo(parseInt(e.target.value) || 100)}
                              placeholder={t('ageGroups.to')}
                              min="0"
                              max="100"
                              className="flex-1 min-w-0 text-center"
                            />
                            <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
                              {t('ageGroups.years')}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  </>
                  )}

                  {/* Additional Instructions */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      {t('stories.additionalInstructions')}
                      <span className="text-gray-400 dark:text-gray-500 font-normal ml-2">
                        ({t('common.optional')})
                      </span>
                    </label>
                    <AutoResizeTextarea
                      value={additionalInstructions}
                      onChange={(e) => setAdditionalInstructions(e.target.value)}
                      placeholder={t('stories.additionalInstructionsPlaceholder')}
                      minRows={2}
                      maxRows={10}
                      className="text-sm"
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {error && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-sm text-red-500"
          >
            {error}
          </motion.p>
        )}

        <div className="flex gap-4">
          {!isEditMode && (
            <Button type="button" variant="secondary" onClick={onCancel}>
              {t('common.cancel')}
            </Button>
          )}
          <Button
            type="submit"
            className="flex-1"
            isLoading={isLoading}
            disabled={selectedMainThemes.length === 0}
          >
            {t('wizard.next')}
          </Button>
        </div>

        {isEditMode && (
          <div className="mt-10 pt-6 border-t border-red-200 dark:border-red-900/50">
            <h3 className="text-sm font-semibold text-red-700 dark:text-red-400 uppercase tracking-wide mb-2">
              {t('editBook.dangerZoneTitle', 'Danger zone')}
            </h3>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 rounded-xl border border-red-200 dark:border-red-900/50 p-4 bg-red-50/40 dark:bg-red-900/10">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t('editBook.deleteBookLabel', 'Delete this book')}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                  {t(
                    'editBook.deleteBookHelp',
                    'Permanently removes the book and all its scenes, drafts, and history. This cannot be undone.'
                  )}
                </p>
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isDeleting}
                className="text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30 border-red-300 dark:border-red-700/50 shrink-0"
              >
                {t('common.delete', 'Delete')}
              </Button>
            </div>
          </div>
        )}
      </form>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        confirmVariant="danger"
        title={t('editBook.deleteBookConfirmTitle', 'Delete this book?')}
        message={t(
          'editBook.deleteBookConfirmBody',
          'This permanently removes the book and all its scenes, drafts, and history. This cannot be undone.'
        )}
        confirmText={t('common.delete', 'Delete')}
        cancelText={t('common.cancel', 'Cancel')}
        onConfirm={handleDelete}
        onClose={() => setShowDeleteConfirm(false)}
      />

      {/* Custom Theme Modal */}
      <AnimatePresence>
        {isCustomThemeModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50"
              onClick={() => {
                setIsCustomThemeModalOpen(false);
                setCustomTheme('');
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-md mx-4 bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                {t('stories.addCustomThemeTitle')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {t('stories.addCustomThemeDescription')}
              </p>
              <Input
                value={customTheme}
                onChange={(e) => setCustomTheme(e.target.value)}
                placeholder={t('stories.customThemePlaceholder')}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCustomTheme();
                  }
                  if (e.key === 'Escape') {
                    setIsCustomThemeModalOpen(false);
                    setCustomTheme('');
                  }
                }}
              />
              <div className="flex gap-3 mt-6">
                <Button
                  type="button"
                  className="flex-1"
                  onClick={addCustomTheme}
                  disabled={!customTheme.trim()}
                >
                  {t('common.add')}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setIsCustomThemeModalOpen(false);
                    setCustomTheme('');
                  }}
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
